import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSoteriaCancelProposal } from '@biasafe.queries/soteria-proposals-proposal-id-cancel/soteria-proposals-proposal-id-cancel';
import { useSoteriaConfirmProposal } from '@biasafe.queries/soteria-proposals-proposal-id-confirm/soteria-proposals-proposal-id-confirm';
import type { AxiosResponse } from 'axios';
import type { CrmProposalCard } from './useSoteriaChat';

/**
 * What the advisor can do about a proposed CRM change, from the server's side.
 *
 * `pending` is the only phase with buttons. The rest are terminal for the card:
 * the server owns the truth (owner, chat, org, expiry, once-only) and this hook
 * only relays it, so a click on a stale card ends in `expired` or `refused`
 * rather than a write.
 */
export type ProposalPhase =
  'pending' | 'confirmed' | 'cancelled' | 'expired' | 'refused' | 'uncertain';

const STALE_MESSAGE =
  'That change expired. Ask Soteria to prepare it again. Nothing was saved.';

function initialPhase(state: string): {
  phase: ProposalPhase;
  message: string | null;
} {
  switch (state) {
    case 'confirmed':
      return { phase: 'confirmed', message: null };
    case 'cancelled':
      return { phase: 'cancelled', message: null };
    case 'expired':
      return { phase: 'expired', message: STALE_MESSAGE };
    case 'failed':
      return {
        phase: 'refused',
        message: 'The CRM refused that change. Nothing was saved.',
      };
    case 'confirming':
      // A save that began and never reported back. It may have landed.
      return {
        phase: 'uncertain',
        message:
          "That change is being saved, or may already be. Check the client's page before asking again.",
      };
    default:
      return { phase: 'pending', message: null };
  }
}

function detailOf(err: unknown): { status?: number; detail?: string } {
  // `response` is absent on a network error, which axios's own type hides.
  const res = (err as { response?: AxiosResponse<{ detail?: unknown }> })
    .response;
  const detail = res?.data.detail;
  return {
    status: res?.status,
    detail: typeof detail === 'string' ? detail : undefined,
  };
}

/**
 * Confirm / cancel one proposal for the chat it was made in.
 *
 * `confirm()` resolves true when the change is saved, including when the server
 * says it already was (a double click or a reload), and false otherwise, so a
 * caller that must act on success (the account draft opening its wizard) can
 * just await it.
 */
export function useCrmProposal(
  proposal: CrmProposalCard,
  sessionId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const confirmMutation = useSoteriaConfirmProposal();
  const cancelMutation = useSoteriaCancelProposal();
  const start = initialPhase(proposal.state);
  const [phase, setPhase] = useState<ProposalPhase>(start.phase);
  const [message, setMessage] = useState<string | null>(start.message);
  const [clientId, setClientId] = useState(proposal.client_id);
  const [summary, setSummary] = useState<string | null>(null);

  const busy = confirmMutation.isPending || cancelMutation.isPending;
  // Two clicks inside one render both see `busy === false`; the server would
  // answer the second with the first's result, but there is no reason to ask.
  const inFlight = useRef(false);

  const refuse = (err: unknown) => {
    const { status, detail } = detailOf(err);
    if (status === 410) {
      setPhase('expired');
      setMessage(STALE_MESSAGE);
      return;
    }
    if (status === undefined || status >= 500 || status === 409) {
      setPhase('uncertain');
      setMessage(
        detail ??
          'The result could not be confirmed. It may already be saved. Check the client record before trying again.',
      );
      return;
    }
    // 404 / 409 are the server saying this card can no longer act (not yours,
    // cancelled, refused, mid-save). 5xx leaves the proposal mid-save on the
    // server, so retrying here would only be refused as well.
    setPhase('refused');
    setMessage(detail ?? 'Could not confirm that change. Nothing was changed.');
  };

  const confirm = async (): Promise<boolean> => {
    if (phase === 'confirmed') return true;
    if (!sessionId || busy || inFlight.current) return false;
    inFlight.current = true;
    try {
      const out = await confirmMutation.mutateAsync({
        proposalId: proposal.id,
        data: { session_id: sessionId },
      });
      setSummary(out.summary ?? null);
      setClientId(out.client_id ?? proposal.client_id);
      setPhase('confirmed');
      toast.success(out.summary ?? 'Saved.');
      // The write may have moved a timeline, a task queue, a status badge.
      void queryClient.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === 'string' &&
          q.queryKey[0].startsWith('/clients'),
      });
      return true;
    } catch (err) {
      // Only a failure frees the guard. After a success the card is about to
      // become a "saved" card, and a click that lands before that re-render
      // must not ask again.
      inFlight.current = false;
      refuse(err);
      return false;
    }
  };

  const cancel = async (): Promise<boolean> => {
    if (!sessionId || busy || inFlight.current) return false;
    inFlight.current = true;
    try {
      const out = await cancelMutation.mutateAsync({
        proposalId: proposal.id,
        data: { session_id: sessionId },
      });
      // Cancelling a proposal that already lapsed answers `expired`; either way
      // nothing was saved and the card is done.
      setPhase(out.state === 'expired' ? 'expired' : 'cancelled');
      if (out.state === 'expired') setMessage(STALE_MESSAGE);
      return true;
    } catch (err) {
      // Only a failure frees the guard. After a success the card is about to
      // become a "saved" card, and a click that lands before that re-render
      // must not ask again.
      inFlight.current = false;
      refuse(err);
      return false;
    }
  };

  return {
    phase,
    message,
    summary,
    clientId,
    busy,
    canAct: !!sessionId,
    confirm,
    cancel,
  };
}
